import React, { useState } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box, 
    Alert, 
    CircularProgress, 
    Typography,
    IconButton,
    InputAdornment,
    Zoom
} from '@mui/material';
import { 
    EmailOutlined as EmailIcon,
    Close as CloseIcon,
    KeyOutlined as KeyIcon,
    CheckCircleOutlineOutlined as CheckCircleIcon
} from '@mui/icons-material';
import { forgotPasswordApi } from '../Api/AuthPermissionsApi';

const BRAND = '#6B31C4';
const BRAND_DARK = '#5a29a5';

// Smooth transition component
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Zoom style={{ transitionDelay: '50ms' }} ref={ref} {...props} />;
});

const ForgotPasswordModal = ({ open, onClose }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleClose = () => {
        setEmail('');
        setError('');
        setSuccess('');
        onClose();
    };

    const handleSendLink = async () => {
        if (!email) {
            setError('Please enter your email.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await forgotPasswordApi({ email });
            setSuccess(response.message || 'Password reset link sent successfully.');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to send reset link.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="xs" 
            fullWidth
            TransitionComponent={Transition}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 1.5,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.12)'
                }
            }}
        >
            {/* Close Button */}
            <IconButton
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: '#9ca3af',
                    '&:hover': { color: '#4b5563' }
                }}
                disabled={loading}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ mt: 1 }}>
                {!success ? (
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        {/* Decorative Key Icon Container */}
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            backgroundColor: `${BRAND}12`,
                            color: BRAND,
                            mb: 2
                        }}>
                            <KeyIcon sx={{ fontSize: 32 }} />
                        </Box>
                        
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                            Forgot Password?
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563', px: 2 }}>
                            Enter your registered email address below. We'll send you a secure link to reset your password.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', my: 2 }}>
                        {/* Success Icon */}
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            backgroundColor: '#ecfdf5',
                            color: '#10b981',
                            mb: 2
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 32 }} />
                        </Box>

                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', mb: 1 }}>
                            Check Your Email
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4b5563', px: 2, mb: 1 }}>
                            We have sent a password reset link to:
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: BRAND, mb: 3 }}>
                            {email}
                        </Typography>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2 }}>
                        {success}
                    </Alert>
                )}

                {!success && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <TextField
                            fullWidth
                            placeholder="name@company.com"
                            variant="outlined"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: '#9ca3af' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 2,
                                    height: 48,
                                    fontSize: '14px',
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: BRAND
                                    }
                                }
                            }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 0, justifyContent: 'center' }}>
                {!success ? (
                    <Button 
                        onClick={handleSendLink} 
                        variant="contained" 
                        disabled={loading} 
                        fullWidth
                        sx={{
                            backgroundColor: BRAND,
                            '&:hover': { backgroundColor: BRAND_DARK },
                            height: 44,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '14px',
                            boxShadow: `0 4px 12px ${BRAND}33`
                        }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Link'}
                    </Button>
                ) : (
                    <Button 
                        onClick={handleClose} 
                        variant="outlined" 
                        fullWidth
                        sx={{
                            borderColor: '#d1d5db',
                            color: '#4b5563',
                            height: 44,
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '14px',
                            '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f9fafb' }
                        }}
                    >
                        Close
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ForgotPasswordModal;
